import React, { ReactElement, ReactNode, useEffect, useState } from 'react';

import { IconWarning, Button } from '@veneer/core';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

import ThemeProviderWrapperV2 from '@storefront/app/ThemeProviderWrapperV2';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { Accordion } from '@storefront/shared-components/accordion/accordion';

import styles from './error-information-visid.module.scss';
import SplunkSessionId from './splunk-session';

interface ReturnLink {
  title: string;
  link: string;
}
interface RetryButtonProps {
  disabled: boolean;
  leadingIcon: ReactNode | null;
}

interface ErrorInformationProps {
  errorId: string;
  title: string;
  description: ReactNode;
  description2?: string;
  code: string;
  buttonDetails?: ButtonDetails;
  errorDetails?: string;
  icon?: ReactElement;
  redirectLink?: ReturnLink;
  retryButtonProps?: RetryButtonProps;
  redirectButtonURL?: string;
}

interface ButtonDetails {
  label: string;
  redirectToURL?: string;
}

interface ErrorInformationWithRedirectButton extends ErrorInformationProps {
  handleClick?: never;
}

interface ErrorInformationWithRetry extends ErrorInformationProps {
  handleClick?: () => unknown;
}

export type ErrorProps =
  | ErrorInformationWithRetry
  | ErrorInformationWithRedirectButton;

function CustomIcon(props: Readonly<{ icon?: ReactElement }>): ReactElement {
  const { icon } = props;
  if (icon) {
    return icon;
  }
  return <IconWarning />;
}

export function ErrorInformation(props: Readonly<ErrorProps>) {
  const { t } = useTranslation('common', { keyPrefix: 'errorScreen' });
  const {
    errorId,
    errorDetails,
    icon,
    code,
    description,
    description2,
    title,
    buttonDetails,
    handleClick,
    redirectLink,
    retryButtonProps,
  } = props;
  const [data] = useState<AccordionItem[]>([
    {
      expanded: false,
      header: {
        centralArea: <span>{t('readMore')}</span>,
      },
      content: errorDetails,
      id: 'accordionId_' + code,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  type AccordionItem = {
    expanded: boolean;
    header: { centralArea: React.ReactElement };
    content: string;
    id: string;
  };

  const handleRedirectButton = async () =>
    (window.location.href = buttonDetails.redirectToURL);
  const handleClickAction = async () => {
    await DataCollection.sendEvent(uiEvents.errorScreenMainBC, {
      screenMode: errorId?.length ? errorId : 'SomethingWentWrong',
      controlDetail: buttonDetails?.label,
    });
    if (handleClick) {
      setIsLoading(true);
      await handleClick();
      setIsLoading(false);
    } else {
      handleRedirectButton();
    }
  };
  const handleClickReturn = async () => {
    await DataCollection.sendEvent(uiEvents.errorScreenReturnBC, {
      screenMode: errorId?.length ? errorId : 'SomethingWentWrong',
    });
  };

  useEffect(() => {
    async function handleEvent() {
      await DataCollection.sendEvent(uiEvents.errorScreenSD, {
        screenMode: errorId?.length ? errorId : 'SomethingWentWrong',
        actionAuxParams: `errorTitle=${title}&errorCode=${code}`,
      });
    }

    handleEvent();
  }, [errorId]);

  return (
    <ThemeProviderWrapperV2>
      <div id="error-container" className={styles['error-container']}>
        <div className={styles['error-icon']}>
          <CustomIcon icon={icon} />
        </div>

        <h1
          className={classNames(styles['error-title'], 'title-small')}
          id={`error-${title}`}
        >
          {title}
        </h1>

        <div
          id="error-desc"
          className={classNames(styles['error-desc'], 'body-regular')}
        >
          {description}
          {description2 && <p>{description2}</p>}
        </div>
        {errorDetails && (
          <>
            <hr className={styles['separator']} />
            <div className={styles['error-read-more']}>
              <Accordion
                style="item"
                customAppearance={'chevron'}
                items={data}
                behavior="singleExpand"
                id="error-info-accordian"
                data-testid="errorInfoAccordion"
              />
            </div>
          </>
        )}
        {buttonDetails && (
          <div className={styles['retry-button']}>
            <Button
              data-testid={'retry-button'}
              disabled={false}
              loading={isLoading}
              onClick={handleClickAction}
              {...retryButtonProps}
            >
              {buttonDetails?.label}
            </Button>
          </div>
        )}
        {redirectLink && (
          <div
            className={classNames(styles['return-link'], 'label-regular')}
            data-testid={'return-link'}
          >
            <Link
              href={redirectLink?.link ?? '/'}
              tabIndex={0}
              onClick={handleClickReturn}
            >
              {redirectLink?.title ?? t('return')}
            </Link>
          </div>
        )}
        <SplunkSessionId />
        <div
          id={`error-code-${code}`}
          className={classNames(styles['error-code'], 'caption-small')}
        >
          {t('code')} {code}
        </div>
      </div>
    </ThemeProviderWrapperV2>
  );
}

export default ErrorInformation;
